import { useSelector } from "react-redux";
import Panel from "../../../../Components/Panels/Panel/panel";
import {
  AddCurrentNodeType,
  AddGlobalVar,
  DeleteDiagramEdgeById,
  DeleteDiagramNodeById,
  GetDiagramEdges,
  GetDiagramNodes,
  GetNodes,
  GetSelectedEdges,
  GetSelectedNodes,
  GetSelectedNodeType,
  GetWorkflow,
  RemoveGlobalVarByIndex,
  SetDiagramEdges,
  SetDiagramNodes,
  SetSelectedNodeType,
  UpdateWorkflow,
  UpdateWorkflowModel,
  UpdateWorkflowVars,
} from "../../../../Redux/Reducers/redux-slice-workflow-designer";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  OutlinedInput,
  Popover,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { produce } from "immer";
import { useDispatch } from "react-redux";
import {
  DeleteOutlineOutlined,
  ErrorOutlineOutlined,
  InfoOutlined,
  InfoRounded,
  SettingsOutlined,
} from "@mui/icons-material";
import { KalikoCondition, KalikoNodeBase, KalikoNodeType, Operator } from "../../../../Core/Api/Types/kaliko";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import { KalikoNodeProps } from "./kaliko-node";
import { NodeTypeSvg } from "./node-picker-panel";
import { KalikoLinkProps } from "./edge";
import CommitIcon from "@mui/icons-material/Commit";
import { useTranslation } from "react-i18next";
import ClearIcon from "@mui/icons-material/Clear";
import FormSection from "../../../../Components/Forms/FormSection/form-section";
import useEventListener from "../../../../Utils/Hooks/useEventListener";
import CodeBox from "../../../../Components/Misc/CodeBox/code-box";
import { deleteNullKeys } from "./utils";

interface NodeConfigurationPanelProps {}

const NodeConfigurationPanel: React.FC<NodeConfigurationPanelProps> = ({}) => {
  const dispatch = useDispatch();
  const selectedNodesIds = useSelector(GetSelectedNodes);
  const nodes = useSelector(GetDiagramNodes);
  const edges = useSelector(GetDiagramEdges);
  const selectedEdgesIds = useSelector(GetSelectedEdges);
  const selectedNodeType = useSelector(GetSelectedNodeType);
  const workflow = useSelector(GetWorkflow);
  const { t } = useTranslation();
  const selectedNodes = useMemo(() => {
    return nodes.filter((x) => selectedNodesIds.includes(x.id));
  }, [nodes, selectedNodesIds]);

  const [localNodes, setLocalNodes] = useState(nodes);
  const [localEdges, setLocalEdges] = useState(edges);
  const [update, setUpdate] = useState(0);
  const [currentField, setCurrentField] = useState<{ nodeId: string; index: number; type: string } | null>(null);

  const availableVariables = useMemo(() => {
    const results: any[] = [];

    for (let index = 0; index < nodes.length; index++) {
      const element = nodes[index];
      if (element.data.defaultOutputParams)
        element.data.defaultOutputParams.forEach((x) =>
          results.push({ ...x, id: element.id, variableName: `[[${element.data.name}.${x.name}]]` })
        );
    }

    const workflowVars = workflow.model.vars;

    for (let index = 0; index < workflowVars.length; index++) {
      const element = workflowVars[index];
      if (element) results.push({ id: -1, variableName: `[[${element.name}]]` });
    }

    return results;
  }, [nodes, workflow?.model?.vars]);

  useEffect(() => {
    dispatch(SetDiagramNodes(localNodes));
    dispatch(SetDiagramEdges(localEdges));
  }, [update]);

  useEffect(() => {
    setLocalNodes(nodes);
  }, [nodes, selectedNodesIds]);

  useEffect(() => {
    setLocalEdges(edges);
  }, [edges, selectedEdgesIds]);

  useEffect(() => {
    if (selectedEdgesIds.length > 0 || selectedNodesIds.length > 0) dispatch(SetSelectedNodeType(null));
  }, [selectedEdgesIds, selectedNodesIds]);

  const selectedItemsCount = useMemo(
    () => selectedNodes.length + edges.filter((x) => x.selected).length,
    [edges, selectedNodes]
  );
  const showWorkflow = useMemo(
    () => selectedItemsCount === 0 && !selectedNodeType,
    [selectedItemsCount, selectedNodeType]
  );
  const theme = useTheme();

  const handleDeleteAllClick = useCallback(() => {
    selectedEdgesIds.forEach((x) => dispatch(DeleteDiagramEdgeById(x)));
    selectedNodesIds.forEach((x) => dispatch(DeleteDiagramNodeById(x)));
  }, [selectedEdgesIds, selectedNodesIds]);

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case "Delete":
          handleDeleteAllClick();
      }
    },
    [handleDeleteAllClick]
  );

  useEventListener("keydown", handleKeydown);

  const nodeNames = useMemo(() => nodes.map((n) => n.data?.name ?? ""), [nodes]);

  const panelHeader = useMemo(
    () => (
      <Stack
        width={"100%"}
        justifyContent={"space-between"}
        height={"48px"}
        alignItems={"center"}
        zIndex={1299}
        borderRadius={"12px 12px 0px 0px"}
        direction={"row"}
        position={"sticky"}
        top={"0px"}
        py={1}
        px={2}
        sx={{ backgroundColor: theme.palette.background.default }}
      >
        <Typography variant="h5">
          {showWorkflow ? t("panel:workflow.settings") : selectedNodeType ?? t("panel:workflow.selectedItems")}
        </Typography>
        <Box>
          {showWorkflow || selectedItemsCount <= 1 ? (
            selectedNodeType ? (
              <NodeTypeSvg type={selectedNodeType} />
            ) : (
              <SettingsOutlined />
            )
          ) : (
            <Tooltip title={t("tooltip:title.deleteSelected")}>
              <Button
                onClick={handleDeleteAllClick}
                endIcon={
                  <Badge badgeContent={selectedItemsCount} color="primary">
                    <DeleteOutlineOutlined color="error" />
                  </Badge>
                }
              >
                {t("button:deleteSelection")}
              </Button>
            </Tooltip>
          )}
        </Box>
      </Stack>
    ),
    [showWorkflow, selectedItemsCount, handleDeleteAllClick, selectedNodeType]
  );
  return (
    <Panel sx={{ position: "relative", height: "auto !important" }} header={panelHeader} open={true} position="right">
      <Stack sx={{ overflow: "auto" }} direction={"column"} spacing={2}>
        {nodes
          .filter((x) => selectedNodesIds.find((i) => i === x.id))
          .map((x, n) => (
            <NodeConfigurationCard
              key={n}
              availableVariables={availableVariables}
              currentField={currentField}
              setCurrentField={setCurrentField}
              setLocalNodes={setLocalNodes}
              setUpdate={setUpdate}
              nodesNames={nodeNames}
              x={x}
            />
          ))}
        {edges
          .filter((x) => selectedEdgesIds.includes(x.id))
          .map((x, i) => (
            <EdgeConfigurationCard
              key={i}
              availableVariables={availableVariables}
              x={x}
              setLocalEdges={setLocalEdges}
              setUpdate={setUpdate}
            />
          ))}
        {selectedNodeType && <NodeTypeDetails selectedNodeType={selectedNodeType} />}
      </Stack>
      {showWorkflow && <WorkflowConfiguration />}
    </Panel>
  );
};

const NodeTypeDetails: React.FC<{ selectedNodeType: KalikoNodeType }> = ({ selectedNodeType }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const node = useSelector(GetNodes).find((x) => x.type === selectedNodeType);

  const handleAddClick = () => {
    dispatch(AddCurrentNodeType());
  };
  if (!node) return <></>;

  return (
    <Stack spacing={1}>
      <CodeBox text={deleteNullKeys(node)} />
      <Divider />
      <Button onClick={handleAddClick}>{t("common:add")}</Button>
    </Stack>
  );
};

interface WorkflowConfigurationProps {}

export const WorkflowConfiguration: React.FC<WorkflowConfigurationProps> = ({}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const workflow = useSelector(GetWorkflow);
  return (
    <Stack spacing={2} direction={"column"}>
      <FormSection defaultExpanded title={t("form:section.title.globalConfig")} size="mini">
        <Stack spacing={1} direction={"column"}>
          <Stack alignItems={"center"} justifyContent={"space-between"} direction={"row"}>
            <Typography color={"text.tier"}>{t("common:name")}</Typography>
            <OutlinedInput
              onChange={(e) => dispatch(UpdateWorkflow({ name: e.target.value }))}
              value={workflow?.name ?? ""}
            />
          </Stack>
          <Stack alignItems={"center"} justifyContent={"space-between"} direction={"row"}>
            <Typography color={"text.tier"}>{t("common:enabled")}</Typography>
            <Switch
              onChange={(e, checked) => dispatch(UpdateWorkflow({ enabled: checked }))}
              checked={workflow?.enabled ?? false}
            />
          </Stack>
        </Stack>
      </FormSection>
      <FormSection title={t("form:section.title.parallelGatewayConfig")} size="mini">
        <Stack spacing={1} direction={"column"}>
          <Typography variant="h6">{t("form:section.title.parallelGatewayConfig")}</Typography>
          <Stack alignItems={"center"} justifyContent={"space-between"} direction={"row"}>
            <Typography color={"text.tier"}>{t("field:label.abortNodeIfFailed")}</Typography>
            <Switch
              onChange={(e, checked) =>
                dispatch(UpdateWorkflowModel({ parallelGatewayAbortNodeIfAnyBranchFailed: checked }))
              }
              checked={workflow?.model.parallelGatewayAbortNodeIfAnyBranchFailed ?? false}
            />
          </Stack>
          <Stack alignItems={"center"} justifyContent={"space-between"} direction={"row"}>
            <Typography color={"text.tier"}>{t("field:label.continueBranchIfFailed")}</Typography>
            <Switch
              onChange={(e, checked) =>
                dispatch(UpdateWorkflowModel({ parallelGatewayContinueBranchesInCaseOfBranchFailed: checked }))
              }
              checked={workflow?.model.parallelGatewayContinueBranchesInCaseOfBranchFailed ?? false}
            />
          </Stack>
        </Stack>
      </FormSection>
      <FormSection title={t("form:section.title.globalVars")} size="mini">
        <Stack direction={"column"} spacing={1}>
          {workflow.model.vars?.map((x, i) => (
            <>
              <Stack direction={"column"} spacing={1}>
                <Stack alignItems={"center"} direction={"row"} justifyContent={"space-between"}>
                  <Typography color={"text.tier"}>{t("common:name")}</Typography>
                  <OutlinedInput
                    onChange={(e) =>
                      dispatch(UpdateWorkflowVars({ v: { name: e.target.value, value: x.value }, index: i }))
                    }
                    value={x.name ?? ""}
                  />
                </Stack>
                <Stack alignItems={"center"} direction={"row"} justifyContent={"space-between"}>
                  <Typography color={"text.tier"}>{t("common:value")}</Typography>
                  <OutlinedInput
                    onChange={(e) =>
                      dispatch(UpdateWorkflowVars({ v: { name: x.name, value: e.target.value }, index: i }))
                    }
                    value={x.value ?? ""}
                  />
                </Stack>
                <Button
                  onClick={() => {
                    dispatch(RemoveGlobalVarByIndex(i));
                  }}
                >
                  {t("common:remove")}
                </Button>
              </Stack>
              {i !== workflow.model.vars.length - 1 && <Divider />}
            </>
          ))}
          <Button
            onClick={() => {
              dispatch(AddGlobalVar());
            }}
          >
            {t("common:add")}
          </Button>
        </Stack>
      </FormSection>
    </Stack>
  );
};

const VarRowContent: React.FC<{ v: { variableName: string; type?: string } }> = ({ v }) => {
  return (
    <CardContent sx={{ p: 0.5 }}>
      <Stack alignItems={"center"} direction={"row"} width={"400px"} justifyContent={"space-between"}>
        <Typography>{v.variableName}</Typography>
        {v.type && <Chip label={v.type} />}
      </Stack>
    </CardContent>
  );
};

interface ConditionPickerProps {
  updateConditionCallback: (
    value: string | Operator,
    id: string,
    field: "left" | "right" | "op",
    append?: boolean
  ) => void;
  clearCondition: (id: string) => void;
  availableVariables: any[];
  condition: KalikoCondition | null;
  id: string;
}
const ConditionPicker: React.FC<ConditionPickerProps> = ({
  updateConditionCallback,
  clearCondition,
  availableVariables,
  condition,
  id,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [anchorElRight, setAnchorElRight] = useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);
  const openRight = Boolean(anchorElRight);
  const popoverId = open ? id : undefined;
  const popoverIdRight = openRight ? `${id}_right` : undefined;

  const handleClosePopover = (e) => {
    setAnchorEl(null);
    setAnchorElRight(null);
  };
  return (
    <FormSection
      size="mini"
      title={t("field:label.condition")}
      icon={
        condition !== null && (
          <IconButton sx={{ height: "16px", width: "16px" }} onClick={() => clearCondition(id)}>
            <ClearIcon color="error" />
          </IconButton>
        )
      }
    >
      <Stack direction={"column"} spacing={1}>
        <Stack display={"flex"} direction={"column"} spacing={1.5}>
          <Stack height={"36px"} direction={"row"} spacing={1}>
            <OutlinedInput
              placeholder={t("field:placeholder.leftCondition")}
              fullWidth
              value={condition?.left ?? ""}
              onChange={(e) => updateConditionCallback(e.target.value, id, "left")}
            />
            <IconButton
              sx={{ height: "36px" }}
              disabled={availableVariables.length === 0}
              onClick={(e) => {
                setAnchorElRight(null);
                setAnchorEl(e.currentTarget);
              }}
            >
              <SettingsInputComponentIcon />
            </IconButton>
            <Popover
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              id={popoverId}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClosePopover}
            >
              <Stack direction={"column"}>
                {availableVariables.map((v, i) => (
                  <Card key={i}>
                    <CardActionArea onClick={(e) => updateConditionCallback(v.variableName, id, "left", true)}>
                      {/* <CardContent sx={{ p: 0.5 }}>
                        <Stack alignItems={"center"} direction={"row"} width={"400px"} justifyContent={"space-between"}>
                          <Typography>{v.variableName}</Typography>
                          <Chip color={!v.type ? "default" : undefined} label={v.type ?? t("common:global")} />
                        </Stack>
                      </CardContent> */}
                      <VarRowContent v={v} />
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            </Popover>
          </Stack>
          <Select
            sx={{ height: "36px" }}
            fullWidth
            value={condition?.op ?? ""}
            onChange={(e) => updateConditionCallback(e.target.value, id, "op")}
          >
            {Object.keys(Operator)
              .filter((el) => {
                return !isNaN(Number(el));
              })
              .map((x, n) => (
                <MenuItem key={n} value={Operator[x]}>
                  {Operator[x]}
                </MenuItem>
              ))}
          </Select>
          <Stack height={"36px"} direction={"row"} spacing={1}>
            <OutlinedInput
              placeholder={t("field:placeholder.rightCondition")}
              fullWidth
              value={condition?.right ?? ""}
              onChange={(e) => updateConditionCallback(e.target.value, id, "right")}
            />
            <IconButton
              sx={{ height: "36px" }}
              disabled={availableVariables.length === 0}
              onClick={(e) => {
                setAnchorEl(null);
                setAnchorElRight(e.currentTarget);
              }}
            >
              <SettingsInputComponentIcon />
            </IconButton>
            <Popover
              sx={{ opacity: 0.9 }}
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              id={popoverIdRight}
              open={openRight}
              anchorEl={anchorElRight}
              onClose={handleClosePopover}
            >
              <Stack direction={"column"}>
                {availableVariables.map((v, i) => (
                  <Card key={i}>
                    <CardActionArea onClick={(e) => updateConditionCallback(v.variableName, id, "right", true)}>
                      <VarRowContent v={v} />
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            </Popover>
          </Stack>
        </Stack>
      </Stack>
    </FormSection>
  );
};

interface NodeConfigurationCardProps {
  availableVariables: any[];
  x: KalikoNodeProps;
  currentField: any;
  setCurrentField: any;
  setLocalNodes: Dispatch<SetStateAction<KalikoNodeProps[]>>;
  setUpdate: Dispatch<SetStateAction<number>>;
  nodesNames: string[];
}
const NodeConfigurationCard: React.FC<NodeConfigurationCardProps> = ({
  x,
  availableVariables,
  currentField,
  setCurrentField,
  setLocalNodes,
  setUpdate,
  nodesNames,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const id = open ? x.id ?? "0" : undefined;

  const handleClosePopover = (e) => {
    setAnchorEl(null);
    setCurrentField(null);
  };

  const updateNodeParams = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | string,
    nodeId: string,
    indexParams: number,
    io: "input" | "output",
    append?: boolean
  ) => {
    setLocalNodes(
      produce((draft) => {
        const updateNode = draft.find((x) => x.id === nodeId);
        if (!updateNode) return;

        if (io === "input") {
          const toUpdateField = updateNode.data?.inputParams && updateNode.data.inputParams[indexParams];
          const newValue = typeof e === "string" ? e : e.target.value;
          if (toUpdateField) toUpdateField.value = append ? `${toUpdateField.value ?? ""}${newValue}` : newValue;
        } else {
          const toUpdateField =
            updateNode.data?.defaultOutputParams && updateNode.data.defaultOutputParams[indexParams];
          const newValue = typeof e === "string" ? e : e.target.value;
          if (toUpdateField) toUpdateField.value = append ? `${toUpdateField.value ?? ""}${newValue}` : newValue;
        }

        draft = [...draft];
      })
    );
    setUpdate((x) => x + 1);
  };

  const updateNodeName = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, nodeId: string) => {
    setLocalNodes(
      produce((draft) => {
        const updateNode = draft.find((x) => x.id === nodeId);
        if (!updateNode) return;

        updateNode.data.name = e.target.value;

        draft = [...draft];
      })
    );
    setUpdate((x) => x + 1);
  };
  const updateNodeCondition = (
    value: string | Operator,
    nodeId: string,
    field: "left" | "right" | "op",
    append?: boolean
  ) => {
    setLocalNodes(
      produce((draft) => {
        const updateNode = draft.find((x) => x.id === nodeId);
        if (!updateNode) return;
        if (!updateNode.data.condition)
          updateNode.data.condition = {
            left: "",
            right: "",
            op: null,
          };
        updateNode.data.condition[field] = append ? `${updateNode.data.condition[field]}${value}` : value;
        draft = [...draft];
      })
    );
    setUpdate((x) => x + 1);
  };

  const handleDeleteClick = () => {
    dispatch(DeleteDiagramNodeById(x.id));
  };

  const clearCondition = (nodeId: string) => {
    setLocalNodes(
      produce((draft) => {
        const updateNode = draft.find((x) => x.id === nodeId);
        if (!updateNode) return;
        updateNode.data.condition = null;
        draft = [...draft];
      })
    );
    setUpdate((x) => x + 1);
  };

  const sameNameExist = useMemo(() => {
    if (!x.data || !x.data.name || x.data.name === "" || !nodesNames || nodesNames.length === 0) return false;
    return nodesNames.filter((n) => n === x.data?.name).length > 1;
  }, [x.data?.name, nodesNames]);

  return (
    <Card sx={{ background: theme.palette.background.default, border: `1px solid ${theme.palette.background.page}` }}>
      <CardHeader
        title={<Typography variant="h6">{x.data.name ?? ""}</Typography>}
        avatar={<NodeTypeSvg type={x.data.type} />}
        action={
          <IconButton onClick={handleDeleteClick}>
            <DeleteOutlineOutlined color="error" />
          </IconButton>
        }
      />
      <CardContent sx={{ paddingTop: "0px" }}>
        <Stack direction={"column"} spacing={1}>
          <FormSection size="mini" defaultExpanded title={t("common:infos")}>
            <Stack height={"36px"} alignItems={"center"} justifyContent={"space-between"} direction={"row"}>
              <Typography color={"text.tier"}>{t("common:name")}</Typography>
              <OutlinedInput
                error={sameNameExist}
                value={x.data.name ?? ""}
                onChange={(e) => updateNodeName(e, x.id)}
              />
            </Stack>
            {sameNameExist && (
              <Alert sx={{ mt: 1, mb: 0.5 }} severity="error" icon={<ErrorOutlineOutlined />}>
                {t("alert:warning.anotherNodeWithSameName")}
              </Alert>
            )}
            <Stack height={"36px"} alignItems={"center"} justifyContent={"space-between"} direction={"row"}>
              <Typography color={"text.tier"}>{t("common:type")}</Typography>
              <Chip label={x.data.type} />
            </Stack>
            <Stack height={"36px"} alignItems={"center"} justifyContent={"space-between"} direction={"row"}>
              <Typography color={"text.tier"}>{t("common:mode")}</Typography>
              <Chip label={x.data.mode} />
            </Stack>
          </FormSection>
          {x.data?.inputParams && (
            <FormSection size="mini" title={t("field:label.inputParams")}>
              <Stack width={"100%"} direction={"column"} spacing={1}>
                {x.data.inputParams.map((o, i) => (
                  <Stack
                    width={"100%"}
                    key={i}
                    alignItems={"center"}
                    direction={"row"}
                    justifyContent={"space-between"}
                    height={"36px"}
                  >
                    <Typography mr={2} flex={2} color={"text.tier"}>
                      {o.name}
                    </Typography>
                    {!o.listOfValues ? (
                      <OutlinedInput
                        placeholder={o.type.toString()}
                        value={o.value ?? ""}
                        onChange={(e) => updateNodeParams(e, x.id, i, "input")}
                        type={o.type.toString() === "String" || o.type.toString() === "Long" ? "text" : "checkbox"}
                      />
                    ) : (
                      <Select
                        sx={{ width: "248px" }}
                        value={o.value ?? ""}
                        onChange={(e) => updateNodeParams(e.target.value, x.id, i, "input")}
                      >
                        {o.listOfValues.map((v) => (
                          <MenuItem value={v}>{v}</MenuItem>
                        ))}
                      </Select>
                    )}
                    {o.type.toString() !== "Boolean" && !o.listOfValues && (
                      <IconButton
                        sx={{ height: "36px" }}
                        disabled={
                          availableVariables.filter((v) => (v.type === o.type || !v.type) && x.id !== v.id).length < 1
                        }
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setCurrentField({ index: i, nodeId: x.id, type: o.type.toString() });
                        }}
                      >
                        <SettingsInputComponentIcon />
                      </IconButton>
                    )}
                    {o.type.toString() !== "Boolean" && !o.listOfValues && (
                      <Popover
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "center",
                        }}
                        transformOrigin={{
                          vertical: "bottom",
                          horizontal: "center",
                        }}
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClosePopover}
                      >
                        <Stack direction={"column"}>
                          {currentField &&
                            availableVariables
                              .filter((v) => currentField.nodeId !== v.id)
                              .map((v, i) => (
                                <Card key={i}>
                                  <CardActionArea
                                    onClick={(e) =>
                                      updateNodeParams(
                                        v.variableName,
                                        currentField.nodeId,
                                        currentField.index,
                                        "input",
                                        true
                                      )
                                    }
                                  >
                                    <VarRowContent v={v} />
                                  </CardActionArea>
                                </Card>
                              ))}
                        </Stack>
                      </Popover>
                    )}
                  </Stack>
                ))}
              </Stack>
            </FormSection>
          )}

          {x.data?.defaultOutputParams && (
            <FormSection size="mini" title={t("field:label.outputParams")}>
              <Stack direction={"column"} spacing={1}>
                {x.data.defaultOutputParams.map((o, i) => (
                  <Stack
                    height={"36px"}
                    key={i}
                    alignItems={"center"}
                    direction={"row"}
                    justifyContent={"space-between"}
                  >
                    <Typography color={"text.tier"}>{o.name}</Typography>
                    <Typography color={"text.secondary"}>{o.type.toString()}</Typography>
                  </Stack>
                ))}
              </Stack>
            </FormSection>
          )}
          <ConditionPicker
            availableVariables={availableVariables}
            condition={x.data.condition ?? null}
            id={x.id}
            updateConditionCallback={updateNodeCondition}
            clearCondition={clearCondition}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

interface EdgeConfigurationCardProps {
  x: KalikoLinkProps;
  setLocalEdges: Dispatch<SetStateAction<KalikoLinkProps[]>>;
  setUpdate: Dispatch<SetStateAction<number>>;
  availableVariables: any[];
}

const EdgeConfigurationCard: React.FC<EdgeConfigurationCardProps> = ({
  x,
  setLocalEdges,
  setUpdate,
  availableVariables,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const nodes = useSelector(GetDiagramNodes);
  const updateEdgeName = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, edgeId: string) => {
    setLocalEdges(
      produce((draft) => {
        const updateEdge = draft.find((x) => x.id === edgeId);
        if (!updateEdge || !updateEdge.data) return;

        updateEdge.data.name = e.target.value;

        draft = [...draft];
      })
    );
    setUpdate((x) => x + 1);
  };

  const updateEdgeCondition = useCallback(
    (value: string | Operator, edgeId: string, field: "left" | "right" | "op", append?: boolean) => {
      setLocalEdges(
        produce((draft) => {
          const updateEdge = draft.find((x) => x.id === edgeId);
          if (!updateEdge || !updateEdge.data) return;
          if (!updateEdge.data.condition)
            updateEdge.data.condition = {
              left: "",
              right: "",
              op: null,
            };
          if (field === "left")
            updateEdge.data.condition.left = append
              ? `${updateEdge.data.condition.left ?? ""}${value as string}`
              : (value as string);
          if (field === "right")
            updateEdge.data.condition.right = append
              ? `${updateEdge.data.condition.right ?? ""}${value as string}`
              : (value as string);
          if (field === "op") updateEdge.data.condition.op = value as Operator;
          draft = [...draft];
        })
      );
      setUpdate((x) => x + 1);
    },
    []
  );

  const handleDeleteClick = () => {
    dispatch(DeleteDiagramEdgeById(x.id));
  };
  const clearCondition = (edgeId: string) => {
    setLocalEdges(
      produce((draft) => {
        const updateEdge = draft.find((x) => x.id === edgeId);
        if (!updateEdge || !updateEdge.data) return;
        updateEdge.data.condition = null;
        draft = [...draft];
      })
    );
    setUpdate((x) => x + 1);
  };
  return (
    <Card sx={{ background: theme.palette.background.default, border: `1px solid ${theme.palette.background.page}` }}>
      <CardHeader
        title={<Typography variant="h6">{t("common:link")}</Typography>}
        sx={{ fontWeight: 900 }}
        avatar={<CommitIcon />}
        action={
          <IconButton onClick={handleDeleteClick}>
            <DeleteOutlineOutlined color="error" />
          </IconButton>
        }
      />
      <CardContent sx={{ paddingTop: "0px" }}>
        <Stack spacing={1}>
          <FormSection size="mini" defaultExpanded title={t("common:infos")}>
            <Stack height={"36px"} alignItems={"center"} justifyContent={"space-between"} direction={"row"}>
              <Typography color={"text.tier"}>{t("common:name")}</Typography>
              <OutlinedInput value={x.data?.name ?? ""} onChange={(e) => updateEdgeName(e, x.id)} />
            </Stack>
            <Stack height={"36px"} alignItems={"center"} justifyContent={"space-between"} direction={"row"}>
              <Typography color={"text.tier"}>{t("common:source")}</Typography>
              <Typography>{nodes.find((node) => node.id === x.source)?.data.name}</Typography>
            </Stack>
            <Stack height={"36px"} alignItems={"center"} justifyContent={"space-between"} direction={"row"}>
              <Typography color={"text.tier"}>{t("common:target")}</Typography>
              <Typography>{nodes.find((node) => node.id === x.target)?.data.name}</Typography>
            </Stack>
          </FormSection>
          <ConditionPicker
            availableVariables={availableVariables}
            condition={x.data?.condition ?? null}
            id={x.id}
            updateConditionCallback={updateEdgeCondition}
            clearCondition={clearCondition}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default NodeConfigurationPanel;
